/**
 * Address search for the location form (plan 7, "Address search").
 * Suggestions come from Photon (photon.komoot.io), built on OpenStreetMap
 * data: free, no key, and results may be stored with the OpenStreetMap credit.
 * Pure: builds the request address and reads the answer; fetching is in
 * src/hooks/useAddressSearch.ts.
 */
import type { AreaConfig } from '../types';
import type { LatLng } from './geo';

export const PHOTON_URL = 'https://photon.komoot.io/api/';

/** Shorter text gives too many far-away results. */
export const ADDRESS_SEARCH_MIN_CHARS = 3;
export const ADDRESS_SEARCH_LIMIT = 6;

/** Photon's 'default' = the local names (Hebrew in Israel). */
export type AddressLang = 'he' | 'en';

export interface AddressHit extends LatLng {
  /** OpenStreetMap id, e.g. "W583798767": the same place in both languages. */
  key: string;
  street: string;
  housenumber?: string;
  city?: string;
}

export function photonSearchUrl(query: string, lang: AddressLang, near: LatLng): string {
  const params = new URLSearchParams({
    q: query.trim(),
    lang: lang === 'he' ? 'default' : 'en',
    lat: String(near.lat),
    lon: String(near.lng),
    // Strong preference for nearby addresses (checked on our streets, 2026-10-28).
    zoom: '14',
    location_bias_scale: '0.1',
    limit: String(ADDRESS_SEARCH_LIMIT + 4),
  });
  params.append('layer', 'house');
  params.append('layer', 'street');
  return `${PHOTON_URL}?${params.toString()}`;
}

interface PhotonFeature {
  geometry?: { coordinates?: unknown };
  properties?: Record<string, unknown>;
}

const HEBREW_LETTERS = /[֐-׿]/;

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

/** Reads Photon's answer: houses and streets only, one per street name + city. */
export function parsePhoton(json: unknown): AddressHit[] {
  const features = (json as { features?: PhotonFeature[] } | null)?.features;
  if (!Array.isArray(features)) return [];
  const hits: AddressHit[] = [];
  const seen = new Set<string>();
  for (const f of features) {
    const p = f.properties ?? {};
    const coords = f.geometry?.coordinates;
    if (!Array.isArray(coords) || typeof coords[0] !== 'number' || typeof coords[1] !== 'number') continue;
    const type = p.type;
    if (type !== 'house' && type !== 'street') continue;
    const housenumber = type === 'house' ? str(p.housenumber) : undefined;
    const street = type === 'house' ? str(p.street) ?? str(p.name) : str(p.name);
    if (!street) continue;
    const city = str(p.city) ?? str(p.county);
    // OpenStreetMap often splits one street into several pieces: show it once.
    const label = `${street}|${housenumber ?? ''}|${city ?? ''}`;
    if (seen.has(label)) continue;
    seen.add(label);
    const osmType = str(p.osm_type) ?? '';
    hits.push({
      key: `${osmType}${String(p.osm_id ?? label)}`,
      street,
      housenumber,
      city,
      lat: Math.round(coords[1] * 1e6) / 1e6,
      lng: Math.round(coords[0] * 1e6) / 1e6,
    });
    if (hits.length >= ADDRESS_SEARCH_LIMIT) break;
  }
  return hits;
}

/** "Nahal Dolev 5", plus the city only when it isn't the area's own city. */
export function formatAddress(hit: AddressHit, areaCity: string | undefined, maxLength = 300): string {
  const base = [hit.street, hit.housenumber].filter(Boolean).join(' ');
  const city = hit.city;
  const sameCity = !city || (!!areaCity && (areaCity.includes(city) || city.includes(areaCity)));
  return (sameCity ? base : `${base}, ${city}`).slice(0, maxLength);
}

/** Both address texts for a picked suggestion; `other` is the same place in the other language. */
export function addressesFromHit(
  picked: AddressHit,
  pickedLang: AddressLang,
  other: AddressHit | undefined,
  area: AreaConfig | undefined,
  maxLength?: number
): { addressHe: string; addressEn: string } {
  const he = pickedLang === 'he' ? picked : other;
  const en = pickedLang === 'en' ? picked : other;
  const addressEn = en ? formatAddress(en, area?.cityEn, maxLength) : '';
  return {
    addressHe: he ? formatAddress(he, area?.cityHe, maxLength) : '',
    // No English name in OpenStreetMap = Photon sends the Hebrew one: leave English for the owner.
    addressEn: HEBREW_LETTERS.test(addressEn) ? '' : addressEn,
  };
}
